import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, ChevronRight, X, Menu } from 'lucide-react';
import { mockLandingData } from '../mock';
import { CustomSectionsAtPosition } from '../components/landing/CustomSection';
import { Preloader } from '../components/landing/Preloader';
import {
  ForWhomSection,
  WhyCoolSection,
  EventFormatSection,
  PersonalBrandSection,
  GallerySection,
  VideoSection,
  TestimonialsSection,
  TicketsSection,
  PartnersSection,
  OrganizersSection
} from '../components/landing/BuiltInSections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Landing = () => {
  const [data, setData] = useState(mockLandingData);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [videoBuffered, setVideoBuffered] = useState(false);
  const [showPreloader, setShowPreloader] = useState(true);
  const [expandedOrganizers, setExpandedOrganizers] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const videoRef = useRef(null);
  const preHeroVideoRef = useRef(null);

  // Load data from backend API in non-critical background thread
  useEffect(() => {
    let isMounted = true;
    setDataLoaded(true);

    const syncLatestData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/landing`);
        if (response.ok) {
          const apiData = await response.json();
          if (isMounted && apiData) setData(apiData);
        }
      } catch (error) {
        console.error('Error syncing latest data from API:', error);
      }
    };
    
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(syncLatestData);
    } else {
      setTimeout(syncLatestData, 200);
    }
    return () => { isMounted = false; };
  }, []);

  // Track scroll position for floating mobile CTA without layout thrashing
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > 380) {
            setShowMobileCta(true);
          } else {
            setShowMobileCta(false);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openGallery = (index) => {
    if (data.gallery && data.gallery[index]) {
      setCurrentImageIndex(index);
      setSelectedImage(data.gallery[index]);
    }
  };

  const nextImage = () => {
    if (!data.gallery || data.gallery.length === 0) return;
    const nextIndex = (currentImageIndex + 1) % data.gallery.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(data.gallery[nextIndex]);
  };

  const prevImage = () => {
    if (!data.gallery || data.gallery.length === 0) return;
    const prevIndex = (currentImageIndex - 1 + data.gallery.length) % data.gallery.length;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(data.gallery[prevIndex]);
  };

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleOrganizerExpand = (id) => {
    setExpandedOrganizers(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleFooterButtonClick = (element) => {
    // Check if this is a special button that should open a modal
    const buttonText = element.text?.toLowerCase() || '';
    
    if (buttonText.includes("зв'язатися") || buttonText.includes('contact')) {
      setContactModalOpen(true);
    } else if (buttonText.includes('конфіденц') || buttonText.includes('privacy')) {
      setPrivacyModalOpen(true);
    } else if (buttonText.includes('оферт') || buttonText.includes('terms')) {
      setTermsModalOpen(true);
    } else if (element.link) {
      window.open(element.link, '_blank');
    }
  };

  // Get header background style
  const getHeaderBgStyle = () => {
    const bgColor = data?.headerBgColor || 'gradient';
    if (bgColor === 'white') {
      return { background: '#ffffff' };
    }
    return { background: 'linear-gradient(to right, #d4af37, #fef5c0, #d4af37)' };
  };

  // Get hero background style
  const getHeroBgStyle = () => {
    if (data?.hero?.bgType === 'image' && data?.hero?.bgImage) {
      return {
        backgroundImage: `url(${data.hero.bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    return { backgroundColor: '#712033' };
  };

  // Helper function to get full URL for uploaded files
  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  };

  // Check if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.m4v'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Check if preHero has video
  const hasPreHeroVideo = Boolean(data?.preHero?.enabled && data?.preHero?.media && isVideoUrl(data.preHero.media));

  // If no preHero video, mark video as buffered immediately once data is loaded
  useEffect(() => {
    if (dataLoaded && !hasPreHeroVideo) {
      setVideoBuffered(true);
    }
  }, [dataLoaded, hasPreHeroVideo]);

  // Desktop video buffering safety timer (1.2s max)
  useEffect(() => {
    if (dataLoaded && showPreloader) {
      const safetyTimer = setTimeout(() => {
        setVideoBuffered(true);
      }, 1200);
      return () => clearTimeout(safetyTimer);
    }
  }, [dataLoaded, showPreloader]);

  const handleVideoProgress = () => {
    const video = preHeroVideoRef.current;
    if (!video) return;
    if (video.buffered && video.buffered.length > 0 && video.duration) {
      const bufferedEnd = video.buffered.end(0);
      const percent = (bufferedEnd / video.duration) * 100;
      if (percent >= 15 || bufferedEnd > 1.5) {
        setVideoBuffered(true);
      }
    }
  };

  const handlePreloaderComplete = () => {
    setShowPreloader(false);
    if (preHeroVideoRef.current) {
      preHeroVideoRef.current.play().catch((err) => {
        console.log('Video play after preloader:', err);
      });
    }
  };

  // Ensure pre-hero video automatically plays on all mobile & desktop browsers (iOS Safari / Android)
  useEffect(() => {
    const video = preHeroVideoRef.current;
    if (video && !showPreloader) {
      video.muted = true;
      video.defaultMuted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          const tryPlay = () => {
            video.play().catch(() => {});
          };
          window.addEventListener('touchstart', tryPlay, { passive: true, once: true });
          window.addEventListener('scroll', tryPlay, { passive: true, once: true });
        });
      }
    }
  }, [data?.preHero?.media, showPreloader]);

  // Get sections config or use default
  const getSectionsConfig = () => {
    return data?.sectionsConfig || [
      { id: 'hero', name: 'Hero', enabled: true, locked: true },
      { id: 'forWhom', name: 'Для кого', enabled: true, locked: false },
      { id: 'whyCool', name: 'Чому класно', enabled: true, locked: false },
      { id: 'eventFormat', name: 'Формат події', enabled: true, locked: false },
      { id: 'personalBrand', name: 'Особистий бренд', enabled: true, locked: false },
      { id: 'gallery', name: 'Галерея', enabled: true, locked: false },
      { id: 'video', name: 'Відео', enabled: true, locked: false },
      { id: 'testimonials', name: 'Відгуки', enabled: true, locked: false },
      { id: 'tickets', name: 'Квитки', enabled: true, locked: false },
      { id: 'partners', name: 'Партнери', enabled: true, locked: false },
      { id: 'organizers', name: 'Організатори', enabled: true, locked: false },
    ];
  };

  // Check if section is enabled
  const isSectionEnabled = (sectionId) => {
    const config = getSectionsConfig();
    const section = config.find(s => s.id === sectionId);
    return section ? section.enabled : true;
  };

  // Get active dynamic navigation links for header & mobile burger
  const getNavLinks = () => {
    if (!data) return [];
    const links = [];
    if (data.forWhom?.length > 0 && isSectionEnabled('forWhom')) {
      links.push({ id: 'for-whom', label: 'Для кого' });
    }
    if (data.whyCool?.length > 0 && isSectionEnabled('whyCool')) {
      links.push({ id: 'why-cool', label: 'Чому класно' });
    }
    if (data.eventFormat?.length > 0 && isSectionEnabled('eventFormat')) {
      links.push({ id: 'event-format', label: 'Формат' });
    }
    if (data.personalBrand && isSectionEnabled('personalBrand')) {
      links.push({ id: 'personal-brand', label: 'Бренд' });
    }
    if (data.gallery?.length > 0 && isSectionEnabled('gallery')) {
      links.push({ id: 'gallery', label: 'Галерея' });
    }
    if (data.testimonials?.length > 0 && isSectionEnabled('testimonials')) {
      links.push({ id: 'testimonials', label: 'Відгуки' });
    }
    if (data.organizers?.length > 0 && isSectionEnabled('organizers')) {
      links.push({ id: 'organizers', label: 'Організатори' });
    }
    if (data.partners && isSectionEnabled('partners')) {
      links.push({ id: 'partners', label: 'Партнери' });
    }
    return links;
  };

  // Get custom sections position mapping (for after each section)
  const getCustomSectionsPositionForSection = (sectionId) => {
    const mapping = {
      'hero': 'after-hero',
      'forWhom': 'after-forwhom',
      'whyCool': 'after-whycool',
      'eventFormat': 'after-format',
      'personalBrand': 'after-brand',
      'gallery': 'after-gallery',
      'video': 'after-video',
      'testimonials': 'after-testimonials',
      'tickets': 'after-tickets',
      'partners': 'after-partners',
      'organizers': 'after-organizers',
    };
    return mapping[sectionId] || `after-${sectionId.toLowerCase()}`;
  };

  // Render a specific section by ID
  const renderSection = (sectionId) => {
    if (!isSectionEnabled(sectionId)) return null;
    
    const customPosition = getCustomSectionsPositionForSection(sectionId);
    
    switch (sectionId) {
      case 'forWhom':
        return (
          <React.Fragment key={sectionId}>
            <ForWhomSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'whyCool':
        return (
          <React.Fragment key={sectionId}>
            <WhyCoolSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'eventFormat':
        return (
          <React.Fragment key={sectionId}>
            <EventFormatSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'personalBrand':
        return (
          <React.Fragment key={sectionId}>
            <PersonalBrandSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'gallery':
        return (
          <React.Fragment key={sectionId}>
            <GallerySection data={data} openGallery={openGallery} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'video':
        return (
          <React.Fragment key={sectionId}>
            <VideoSection data={data} videoRef={videoRef} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'testimonials':
        return (
          <React.Fragment key={sectionId}>
            <TestimonialsSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'tickets':
        return (
          <React.Fragment key={sectionId}>
            <TicketsSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'partners':
        return (
          <React.Fragment key={sectionId}>
            <PartnersSection data={data} />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      case 'organizers':
        return (
          <React.Fragment key={sectionId}>
            <OrganizersSection 
              data={data} 
              expandedOrganizers={expandedOrganizers} 
              toggleOrganizer={toggleOrganizerExpand} 
            />
            {customPosition && <CustomSectionsAtPosition customSections={data.customSections} position={customPosition} />}
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  // Render all sections in order from config
  const renderAllSections = () => {
    const config = getSectionsConfig();
    // Filter out hero (rendered separately) and disabled sections
    return config
      .filter(section => section.id !== 'hero')
      .map(section => renderSection(section.id));
  };

  // Get page background style
  const getPageBgStyle = () => {
    if (data?.usePageBgColor && data?.pageBgColor) {
      return { backgroundColor: data.pageBgColor };
    }
    return {};
  };

  return (
    <div className="landing-page" style={getPageBgStyle()}>
      {/* Interactive Preloader Screen */}
      {showPreloader && (
        <Preloader
          isReady={dataLoaded && (videoBuffered || !hasPreHeroVideo)}
          onComplete={handlePreloaderComplete}
        />
      )}

      {/* Sticky Navigation Header */}
      <header className="sticky-header" style={getHeaderBgStyle()}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 group focus:outline-none" aria-label="Gastro Bloggers Day">
            <img 
              src="/logo.webp" 
              alt="Gastro Bloggers Day" 
              width="160"
              height="48"
              style={{ aspectRatio: '160/48' }}
              className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
              fetchpriority="high"
              decoding="async"
            />
          </a>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-6">
            {getNavLinks().map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => scrollToSection(e, link.id)}
                className="nav-link"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Header Action Buttons */}
          <div className="flex items-center gap-3">
            <Button 
              asChild
              className="gold-gradient-btn px-5 py-2 text-sm md:text-base rounded-full shadow-md"
            >
              <a href="#tickets" onClick={(e) => scrollToSection(e, 'tickets')}>
                Купити квиток
              </a>
            </Button>

            {/* Mobile Menu Toggle Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              aria-label="Відкрити меню навігації"
              className="lg:hidden p-2 text-[#712033] hover:text-[#5a1626] hover:bg-[#712033]/10 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#712033]/15 px-6 py-4 space-y-2 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-150">
            {getNavLinks().map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => scrollToSection(e, link.id)}
                className="block py-2.5 text-[#712033] font-bold border-b border-[#712033]/10 hover:text-[#ce657d] transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </header>

      {/* Main Content Landmark */}
      <main id="main-content">
        {/* Pre-Hero Section - Photo or Looping Video */}
        {data.preHero?.enabled && data.preHero?.media && (
          <section 
            className="pre-hero-section w-full aspect-video overflow-hidden bg-[#712033]" 
            style={{ aspectRatio: '16/9' }}
            data-testid="pre-hero-section"
          >
            {isVideoUrl(data.preHero.media) ? (
              <video
                ref={preHeroVideoRef}
                className="w-full h-full aspect-video object-cover"
                width="1920"
                height="1080"
                style={{ aspectRatio: '16/9' }}
                autoPlay
                loop
                muted
                defaultMuted
                playsInline
                preload="auto"
                onProgress={handleVideoProgress}
                onLoadedData={handleVideoProgress}
                onCanPlayThrough={handleVideoProgress}
                data-testid="pre-hero-video"
              >
                <source src={getMediaUrl(data.preHero.media)} type="video/mp4" />
              </video>
            ) : (
              <img 
                src={getMediaUrl(data.preHero.media)} 
                alt="Pre-hero" 
                width="1920"
                height="1080"
                style={{ aspectRatio: '16/9' }}
                className="w-full h-full aspect-video object-cover"
                data-testid="pre-hero-image"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            )}
          </section>
        )}

        {/* Hero Section */}
        <section className="hero-section relative text-white py-20 px-6 md:px-12 overflow-hidden" style={getHeroBgStyle()}>
          {/* Overlay for image background */}
          {data.hero?.bgType === 'image' && data.hero?.bgImage && (
            <div className="absolute inset-0 bg-black/50"></div>
          )}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
          </div>
          <div className="max-w-6xl mx-auto relative z-10">
            <h1 
              className="hero-headline text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight"
              style={{ textAlign: data.hero?.headlineAlign || 'left' }}
            >
              {data.hero?.headline ? data.hero.headline.split('\n').map((line, i) => (
                <span key={i} className="block">{line}</span>
              )) : 'Gastro Bloggers Day'}
            </h1>
            <p 
              className="hero-subheadline text-base md:text-xl mb-6 max-w-4xl opacity-90 leading-relaxed"
              style={{ textAlign: data.hero?.subheadlineAlign || 'left' }}
            >
              {data.hero?.subheadline}
            </p>
            <p 
              className="hero-details text-base md:text-lg mb-12 gold-gradient-text font-bold"
              style={{ textAlign: data.hero?.detailsAlign || 'left' }}
            >
              {data.hero?.details}
            </p>
            <div className="hero-buttons flex flex-wrap gap-4 justify-center md:justify-start">
              {data.hero?.buttons?.map((btn, idx) => (
                <Button
                  key={idx}
                  asChild
                  className={`w-full md:w-auto ${btn.type === 'primary' ? 'gold-gradient-btn' : 'secondary-btn'}`}
                  data-testid={`hero-button-${idx}`}
                >
                  <a href={btn.link} target="_blank" rel="noopener noreferrer">
                    {btn.text}
                  </a>
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Sections - After Hero */}
        <CustomSectionsAtPosition customSections={data.customSections} position="after-hero" />

        {/* All Sections - Dynamic Rendering based on sectionsConfig */}
        {renderAllSections()}
      </main>

      {/* Mobile Floating Sticky CTA Button (Visible on scroll past Hero on mobile) */}
      {showMobileCta && (
        <div className="md:hidden mobile-floating-cta-container">
          <a
            href="#tickets"
            onClick={(e) => scrollToSection(e, 'tickets')}
            className="mobile-floating-cta-btn gold-gradient-btn"
            data-testid="mobile-floating-cta"
          >
            🎟️ Купити квиток
          </a>
        </div>
      )}

      {/* Footer */}
      <footer className="footer bg-[#712033] text-white py-12 px-6 md:px-12 border-t-4 border-[#ce657d]">
        <div className="max-w-6xl mx-auto text-center">
          {data.footer && data.footer.elements && data.footer.elements.map((element) => {
            if (element.type === 'text') {
              if (element.style === 'title') {
                return (
                  <p key={element.id} className="text-xl font-bold mb-4">
                    {element.content}
                  </p>
                );
              } else if (element.style === 'subtitle') {
                return (
                  <p key={element.id} className="mb-6 opacity-90">
                    {element.content}
                  </p>
                );
              } else {
                return (
                  <p key={element.id} className="mb-4">
                    {element.content}
                  </p>
                );
              }
            } else if (element.type === 'button') {
              // Check if it's a special modal-triggering button
              const btnText = element.text?.toLowerCase() || '';
              const isContactBtn = btnText.includes("зв'язатися") || btnText.includes('contact');
              const isPrivacyBtn = btnText.includes('конфіденц') || btnText.includes('privacy');
              const isTermsBtn = btnText.includes('оферт') || btnText.includes('terms');
              
              if (isContactBtn || isPrivacyBtn || isTermsBtn) {
                return (
                  <div key={element.id} className="mb-4">
                    <Button 
                      className="gold-gradient-btn cursor-pointer"
                      onClick={() => handleFooterButtonClick(element)}
                      data-testid={`footer-btn-${element.id}`}
                    >
                      {element.text}
                    </Button>
                  </div>
                );
              }
              
              return (
                <div key={element.id} className="mb-4">
                  <Button asChild className="gold-gradient-btn">
                    <a href={element.link} target="_blank" rel="noopener noreferrer">
                      {element.text}
                    </a>
                  </Button>
                </div>
              );
            } else if (element.type === 'link') {
              // Text links for privacy/terms
              const linkText = element.text?.toLowerCase() || '';
              const isPrivacyLink = linkText.includes('конфіденц') || linkText.includes('privacy');
              const isTermsLink = linkText.includes('оферт') || linkText.includes('terms');
              
              return (
                <div key={element.id} className="mb-4">
                  <button
                    onClick={() => {
                      if (isPrivacyLink) setPrivacyModalOpen(true);
                      else if (isTermsLink) setTermsModalOpen(true);
                      else if (element.link) window.open(element.link, '_blank');
                    }}
                    className="text-white/80 hover:text-white underline transition-colors cursor-pointer"
                    data-testid={`footer-link-${element.id}`}
                  >
                    {element.text}
                  </button>
                </div>
              );
            }
            return null;
          })}
        </div>
      </footer>

      {/* Gallery Lightbox Modal */}
      {selectedImage && (
        <div 
          className="modal-overlay z-[110]" 
          onClick={() => setSelectedImage(null)} 
          data-testid="gallery-modal"
        >
          <div 
            className="relative max-w-4xl w-full max-h-[90vh] bg-black/95 p-3 md:p-6 rounded-2xl flex flex-col items-center justify-center overflow-hidden border border-white/10 shadow-2xl" 
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="absolute top-3 right-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors z-20" 
              onClick={() => setSelectedImage(null)} 
              aria-label="Закрити фото"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="relative w-full h-full flex items-center justify-center my-3">
              <img 
                src={getMediaUrl(selectedImage.url)} 
                alt={selectedImage.alt || 'Gastro Bloggers Day'} 
                className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-lg"
              />

              <button 
                onClick={prevImage} 
                aria-label="Попереднє фото" 
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all border border-white/20"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button 
                onClick={nextImage} 
                aria-label="Наступне фото" 
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all border border-white/20"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            <div className="text-white/80 text-sm font-medium">
              {currentImageIndex + 1} / {data.gallery?.length || 0}
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {contactModalOpen && (
        <div className="modal-overlay" onClick={() => setContactModalOpen(false)} data-testid="contact-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setContactModalOpen(false)} aria-label="Закрити вікно" data-testid="contact-modal-close">
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold text-[#712033] mb-6 text-center">Зв&#39;язатися з нами</h3>
            <div className="space-y-4">
              {data.contactModal?.buttons?.map((btn, idx) => (
                <a
                  key={idx}
                  href={btn.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`contact-btn-${idx}`}
                >
                  <span className="text-2xl">{btn.icon}</span>
                  <span className="font-semibold text-[#712033]">{btn.text}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {privacyModalOpen && (
        <div className="modal-overlay" onClick={() => setPrivacyModalOpen(false)} data-testid="privacy-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setPrivacyModalOpen(false)} aria-label="Закрити вікно" data-testid="privacy-modal-close">
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold text-[#712033] mb-6">Політика конфіденційності</h3>
            <div className="prose text-gray-700 whitespace-pre-line">
              {data.privacyPolicy || "Текст політики конфіденційності..."}
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {termsModalOpen && (
        <div className="modal-overlay" onClick={() => setTermsModalOpen(false)} data-testid="terms-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setTermsModalOpen(false)} aria-label="Закрити вікно" data-testid="terms-modal-close">
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold text-[#712033] mb-6">Договір оферти</h3>
            <div className="prose text-gray-700 whitespace-pre-line">
              {data.termsOfService || "Текст договору оферти..."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
